import React from "react";
import { Box, Card, CardContent, Divider, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ColoredChips from "shared-ui/src/theme/extensions/ColoredChips";
import shiColors from "shared-ui/src/theme/shiColors";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";

// Shared presentation primitives for the Quotes pages, ported from the designer mockups
// (ShiOne-mockups · prototype-ui). Kept in one module so the list, detail, and create pages render
// status, availability, currency, quantity controls, and side cards consistently.

export type QuoteStatus = "Active" | "Expired";

// Surface colors come from the theme palette (light/dark aware) so the quote pages track dark mode:
//   table header  -> palette.table.header
//   cards / surfaces -> palette.background.paper
//   composer canvas & step title areas -> palette.background.default
// Reference them in sx as bgcolor: "table.header" / "background.paper" / "background.default".

export function fmtCurrency(value: number | null | undefined, currencyId = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return createSavedCurrencyFormatter(currencyId).format(value);
}

// SHI judges quote expiry in its business timezone (US Eastern). A quote is valid THROUGH the end of
// its expiry day, so we compare calendar dates — expiry has no meaningful time-of-day. This mirrors
// the backend (QuoteDetailMapper) so the list and detail pages agree, and a quote expiring today is
// not treated as expired until the next business day.
const SHI_BUSINESS_TIME_ZONE = "America/New_York";

// Calendar date (YYYY-MM-DD, lexicographically comparable) of an instant in SHI's business timezone.
function easternDateString(instant: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: SHI_BUSINESS_TIME_ZONE }).format(instant);
}

export function isQuoteExpired(expiresOn: Date | string | null | undefined): boolean {
  if (!expiresOn) return false;
  // expiresOn is a date-only value; read its stored calendar date without applying a timezone shift.
  const expiryDate =
    typeof expiresOn === "string"
      ? expiresOn.split("T")[0]
      : expiresOn.toISOString().split("T")[0];
  return expiryDate < easternDateString(new Date());
}

// Status chip using the shared ColoredChips + shiColors, matching the mockup (green = Active,
// red = Expired) instead of MUI's default success/error chips.
export function QuoteStatusChip({ status }: { status: QuoteStatus }) {
  return (
    <ColoredChips
      label={status}
      borderColor={status === "Active" ? shiColors.green.dark : shiColors.red.DEFAULT}
      display="inline-flex"
    />
  );
}

// Bordered +/- quantity stepper from the mockup (replaces bare icon buttons on the composer).
export function QuantitySpinner({
  value,
  onChange,
  min = 0,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        height: 32,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <IconButton
        size="small"
        aria-label="decrease quantity"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        sx={{ borderRadius: 0, px: 0.75, color: "text.secondary" }}
      >
        <RemoveIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Box
        role="status"
        aria-live="polite"
        aria-label={`Quantity: ${value}`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 36,
          px: 0.5,
          borderLeft: "1px solid",
          borderRight: "1px solid",
          borderColor: "divider",
          fontSize: 14,
        }}
      >
        {value}
      </Box>
      <IconButton
        size="small"
        aria-label="increase quantity"
        onClick={() => onChange(value + 1)}
        sx={{ borderRadius: 0, px: 0.75, color: "text.secondary" }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

// Outlined side card with an overline title + divider, used for the detail page's
// Shipping Address / Contact / Account Team panels.
export function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined">
      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Divider />
      <CardContent sx={{ pt: 1.5 }}>{children}</CardContent>
    </Card>
  );
}

// A small icon + text row used inside SectionCard (Contact / Account Team / Shipping Address).
export function InfoRow({
  icon,
  children,
  align = "center",
  emphasize,
  mt,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  align?: "center" | "flex-start";
  emphasize?: boolean;
  mt?: number;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: align, gap: 0.75, mt }}>
      <Box
        sx={{
          color: "text.secondary",
          display: "flex",
          mt: align === "flex-start" ? "2px" : 0,
          flexShrink: 0,
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, color: emphasize ? "primary.main" : undefined }}>{children}</Box>
    </Box>
  );
}
